import { Mat4, Vec3 } from 'playcanvas';

import type { Element } from './element';
import type { Scene } from './scene';

const tmpMatA = new Mat4();
const tmpMatB = new Mat4();
const tmpScale = new Vec3();

const isNonUniformScale = (s: Vec3, eps = 1e-4) => {
    return Math.abs(s.x - s.y) > eps || Math.abs(s.x - s.z) > eps || Math.abs(s.y - s.z) > eps;
};

const fireStatus = (scene: Scene, text: string) => {
    // Optional lightweight status messaging; safe no-op if no listeners exist.
    try {
        scene?.events?.fire?.('status.text', text, { timeoutMs: 2500 });
    } catch (e) {
        // best effort
    }
};

const removeAllFromArray = <T>(arr: T[], item: T) => {
    // In case of prior duplication bugs, remove all occurrences.
    for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i] === item) arr.splice(i, 1);
    }
};

// Cinema4D-style reparenting: child stays visually in place; local TRS becomes relative to the parent.
//
// NOTE: Cross-type parenting is intentional in NSite (mesh under splat, splat under helper, etc.).
// This function treats all Elements uniformly and relies on cycle/entity-cycle checks for safety.
// Returns true if reparented, false if rejected (eg cycle).
const reparentPreserveWorld = (child: Element, newParent: Element | null, scene: Scene): boolean => {
    if (!child || !scene) return false;
    if (!(scene as any).contentRoot) {
        console.warn('reparentPreserveWorld: scene.contentRoot is missing');
        return false;
    }

    // Cross-scene guard (best effort). All elements involved must belong to the same scene.
    if ((child as any).scene && (child as any).scene !== scene) return false;
    if (newParent && (newParent as any).scene && (newParent as any).scene !== scene) return false;

    const oldParent = (child as any).parentElement as Element | null;
    if (oldParent === newParent) return false;

    // Cycle check: reject if newParent is the child or a descendant of the child.
    // Also protects against a pre-existing cycle in parentElement pointers (avoid infinite loop).
    const visited = new Set<Element>();
    let p: Element | null = newParent;
    while (p) {
        if (p === child) {
            fireStatus(scene, 'Cannot parent: would create a cycle.');
            return false;
        }
        if (visited.has(p)) {
            console.warn('reparentPreserveWorld: detected existing cycle in parentElement chain; refusing to reparent', {
                child,
                newParent
            });
            fireStatus(scene, 'Cannot parent: hierarchy is corrupted (cycle detected).');
            return false;
        }
        visited.add(p);
        p = (p as any).parentElement ?? null;
    }

    const childEntity = (child as any).entity as any;
    if (!childEntity?.reparent || !childEntity?.getWorldTransform) {
        console.warn('reparentPreserveWorld: child has no entity with reparent/getWorldTransform', child);
        return false;
    }

    const targetParentEntity = (newParent ? (newParent as any).entity : scene.contentRoot) as any;
    if (!targetParentEntity?.getWorldTransform) {
        console.warn('reparentPreserveWorld: newParent has no entity/getWorldTransform', newParent);
        return false;
    }

    // Safety: do not allow reparenting into the child's own entity subtree (entity-cycle).
    // This can happen if logical parentElement pointers drift from the actual entity hierarchy.
    let pe: any = targetParentEntity;
    while (pe) {
        if (pe === childEntity) {
            fireStatus(scene, 'Cannot parent: would create an entity cycle.');
            return false;
        }
        pe = pe.parent ?? null;
    }

    // Best-effort guard: non-uniform scale on the target parent can cause subtle scale drift
    // when extracting scale from a decomposed (potentially sheared) matrix.
    const targetScale = targetParentEntity.getWorldTransform().getScale(tmpScale);
    if (isNonUniformScale(targetScale)) {
        fireStatus(scene, 'Warning: non-uniform scale on parent may cause subtle drift when parenting.');
    }

    // Save world TRS
    const savedPos = childEntity.getPosition().clone();
    const savedRot = childEntity.getRotation().clone();

    // Compute local scale relative to the target parent.
    // localMat = inv(parentWorld) * childWorld
    const parentWorld = targetParentEntity.getWorldTransform();
    const childWorld = childEntity.getWorldTransform();
    tmpMatA.copy(parentWorld).invert();
    // NOTE: Avoid using the output matrix as an input operand (aliasing risk in mul2 implementation).
    tmpMatB.mul2(tmpMatA, childWorld);
    tmpMatB.getScale(tmpScale);
    const newLocalScale = tmpScale.clone();

    // Reparent via PlayCanvas native
    try {
        childEntity.reparent(targetParentEntity);
    } catch (e) {
        console.warn('reparentPreserveWorld: entity.reparent failed', e);
        return false;
    }

    // Update bookkeeping (after successful entity reparent)
    if (oldParent) {
        const siblings = (oldParent as any).childElements as Element[] | undefined;
        if (Array.isArray(siblings)) removeAllFromArray(siblings, child);
    }

    (child as any).parentElement = newParent;
    if (newParent) {
        const kids = (newParent as any).childElements as Element[] | undefined;
        if (Array.isArray(kids) && !kids.includes(child)) kids.push(child);
    }

    // Restore world TRS. (PlayCanvas derives local from world)
    childEntity.setPosition(savedPos);
    childEntity.setRotation(savedRot);
    childEntity.setLocalScale(newLocalScale);

    scene.forceRender = true;
    scene.events.fire('hierarchy.changed', { child, oldParent, newParent });
    return true;
};

export { reparentPreserveWorld };
